import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Clock, MapPin, UserRound, Star, RefreshCw, CheckCircle2, Camera, X, ChevronLeft, ChevronRight, ClipboardCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { clientGet, errorMessage, isUnauthorized, fmtDate, fmtTime, fmtMinutes, reportPhotoUrl, qcPhotoUrl } from '../../lib/clientApi';
import { StatusBadge, EmptyState, ContactAgencyButton, Stars } from './shared';
import { RatingDialog } from './RatingDialog';

const KIND_CLS = { before: 'bg-[hsl(var(--brand-sky))] text-[hsl(var(--brand-ink))]', after: 'bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))]', other: 'bg-secondary text-foreground' };

/** Before/after photos of a past intervention: thumbnails + full-screen viewer. */
function InterventionPhotos({ ev }) {
  const [open, setOpen] = useState(null); // index in ev.photos
  if (!ev.photos?.length && !ev.report_note) return null;
  const current = open !== null ? ev.photos[open] : null;
  return (
    <div className="mt-3 pt-3 border-t border-border/60" data-testid="client-intervention-photos">
      <p className="text-sm font-semibold flex items-center gap-1.5"><Camera className="h-4 w-4" /> Compte-rendu de votre Easer</p>
      {ev.report_note && <p className="text-base text-muted-foreground mt-1 whitespace-pre-line" data-testid="client-intervention-report-note">{ev.report_note}</p>}
      {ev.photos?.length > 0 && (
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
          {ev.photos.map((p, i) => (
            <button key={`${p.report_id}-${p.index}`} type="button" onClick={() => setOpen(i)} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]" aria-label={`Photo ${p.kind_label}`} data-testid="client-intervention-photo-thumb">
              {p.thumb ? <img src={p.thumb} alt={`${p.kind_label} — ${ev.label}`} className="h-full w-full object-cover" loading="lazy" /> : <Camera className="h-6 w-6 m-auto text-muted-foreground" />}
              <span className={`absolute bottom-1 left-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${KIND_CLS[p.kind] || KIND_CLS.other}`}>{p.kind_label}</span>
            </button>
          ))}
        </div>
      )}
      <Dialog open={open !== null} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <DialogContent className="max-w-3xl p-2 bg-black/95 border-none [&>button]:hidden" data-testid="client-photo-viewer">
          <DialogTitle className="sr-only">{current ? `Photo ${current.kind_label}` : 'Photo'}</DialogTitle>
          {current && (
            <div className="relative">
              <img src={reportPhotoUrl(current.report_id, current.index)} alt={`${current.kind_label} — ${ev.label}`} className="max-h-[80vh] w-full object-contain rounded-md" />
              <span className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-xs font-semibold ${KIND_CLS[current.kind] || KIND_CLS.other}`}>{current.kind_label}</span>
              <button type="button" onClick={() => setOpen(null)} className="absolute top-2 right-2 h-10 w-10 rounded-full bg-black/60 text-white flex items-center justify-center" aria-label="Fermer" data-testid="client-photo-viewer-close"><X className="h-5 w-5" /></button>
              {ev.photos.length > 1 && (
                <>
                  <button type="button" onClick={() => setOpen((open + ev.photos.length - 1) % ev.photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/60 text-white flex items-center justify-center" aria-label="Photo précédente" data-testid="client-photo-viewer-prev"><ChevronLeft className="h-6 w-6" /></button>
                  <button type="button" onClick={() => setOpen((open + 1) % ev.photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/60 text-white flex items-center justify-center" aria-label="Photo suivante" data-testid="client-photo-viewer-next"><ChevronRight className="h-6 w-6" /></button>
                </>
              )}
              <p className="text-center text-white/80 text-sm mt-2">{open + 1} / {ev.photos.length}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Contrôle qualité réalisé par un manager de l'agence et mis à disposition du client : étoiles par critère, compte rendu, photos. */
function QualityControlBlock({ qc, label }) {
  const [open, setOpen] = useState(null);
  if (!qc) return null;
  const avg = qc.average === null || qc.average === undefined ? '—' : Number(qc.average).toFixed(1).replace('.', ',');
  const current = open !== null ? qc.photos[open] : null;
  return (
    <div className="mt-3 pt-3 border-t border-border/60" data-testid="client-quality-control">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-semibold flex items-center gap-1.5"><ClipboardCheck className="h-4 w-4 text-[hsl(var(--primary))]" /> Contrôle qualité de l'agence</p>
        <span className="inline-flex items-center gap-2 text-base"><Stars value={qc.average} size="h-5 w-5" testId="client-quality-stars" /><span className="font-semibold" data-testid="client-quality-average">{avg} / 5</span></span>
      </div>
      <p className="text-sm text-muted-foreground mt-0.5">Réalisé par {qc.manager_name || 'un manager'} le {fmtDate(qc.created_at, { day: 'numeric', month: 'long', year: 'numeric' })}</p>
      <ul className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1" data-testid="client-quality-criteria">
        {(qc.criteria || []).map((c) => (
          <li key={c.id} className="flex items-center justify-between gap-2 text-base"><span>{c.label}</span><Stars value={c.stars} size="h-4 w-4" /></li>
        ))}
      </ul>
      {qc.report && <p className="text-base text-muted-foreground mt-2 whitespace-pre-line" data-testid="client-quality-report">{qc.report}</p>}
      {qc.photos?.length > 0 && (
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
          {qc.photos.map((p, i) => (
            <button key={p.index} type="button" onClick={() => setOpen(i)} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]" aria-label={p.caption || `Photo ${i + 1}`} data-testid="client-quality-photo-thumb">
              {p.thumb ? <img src={p.thumb} alt={p.caption || `Contrôle qualité — ${label}`} className="h-full w-full object-cover" loading="lazy" /> : <Camera className="h-6 w-6 m-auto text-muted-foreground" />}
            </button>
          ))}
        </div>
      )}
      <Dialog open={open !== null} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <DialogContent className="max-w-3xl p-2 bg-black/95 border-none [&>button]:hidden" data-testid="client-quality-photo-viewer">
          <DialogTitle className="sr-only">Photo du contrôle qualité</DialogTitle>
          {current && (
            <div className="relative">
              <img src={qcPhotoUrl(qc.id, current.index)} alt={current.caption || `Contrôle qualité — ${label}`} className="max-h-[80vh] w-full object-contain rounded-md" />
              <button type="button" onClick={() => setOpen(null)} className="absolute top-2 right-2 h-10 w-10 rounded-full bg-black/60 text-white flex items-center justify-center" aria-label="Fermer" data-testid="client-quality-photo-close"><X className="h-5 w-5" /></button>
              {qc.photos.length > 1 && (
                <>
                  <button type="button" onClick={() => setOpen((open + qc.photos.length - 1) % qc.photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/60 text-white flex items-center justify-center" aria-label="Photo précédente"><ChevronLeft className="h-6 w-6" /></button>
                  <button type="button" onClick={() => setOpen((open + 1) % qc.photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/60 text-white flex items-center justify-center" aria-label="Photo suivante"><ChevronRight className="h-6 w-6" /></button>
                </>
              )}
              {current.caption && <p className="text-center text-white/80 text-sm mt-2">{current.caption}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ClientInterventions({ onUnauthorized }) {
  const [scope, setScope] = useState('upcoming');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await clientGet('/api/client/interventions', { scope });
      setItems(res.data.items || []);
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      setError(errorMessage(err, 'Impossible de charger vos interventions'));
    } finally { setLoading(false); }
  }, [scope, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const now = Date.now();

  return (
    <div className="space-y-4" data-testid="client-interventions">
      <div className="flex items-center gap-2">
        <h1 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight">Mes interventions</h1>
        <Button variant="ghost" size="icon" className="ml-auto h-11 w-11" onClick={load} aria-label="Actualiser" data-testid="client-interventions-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      <Tabs value={scope} onValueChange={setScope} data-testid="client-interventions-tabs">
        <TabsList className="grid grid-cols-2 w-full h-12">
          <TabsTrigger value="upcoming" className="text-base h-10" data-testid="client-interventions-tab-upcoming">À venir</TabsTrigger>
          <TabsTrigger value="past" className="text-base h-10" data-testid="client-interventions-tab-past">Passées</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading && <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-[var(--radius)]" />)}</div>}

      {!loading && error && (
        <Card className="p-4 border-destructive/30" role="alert" data-testid="client-interventions-error">
          <p className="text-base">{error}</p>
          <Button variant="outline" className="mt-3 h-11" onClick={load} data-testid="client-interventions-retry">Réessayer</Button>
        </Card>
      )}

      {!loading && !error && items.length === 0 && (scope === 'upcoming'
        ? <EmptyState icon={CalendarDays} title="Aucune intervention prévue" copy="Besoin d'un service ? Contactez l'agence pour planifier une intervention." action={<ContactAgencyButton />} testId="client-interventions-upcoming-empty" />
        : <EmptyState icon={CheckCircle2} title="Aucune intervention passée" copy="Votre historique d'interventions apparaîtra ici." testId="client-interventions-past-empty" />)}

      {!loading && items.map((ev) => {
        const inProgress = scope === 'upcoming' && ev.start && new Date(ev.start).getTime() <= now;
        return (
          <Card key={ev.id} className="p-4 shadow-sm border-border" data-testid="client-intervention-card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-base font-semibold" data-testid="client-intervention-label">{ev.label}</p>
                <p className="text-base text-muted-foreground capitalize mt-0.5" data-testid="client-intervention-date">{fmtDate(ev.start, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </div>
              <StatusBadge state={scope === 'past' ? 'done' : inProgress ? 'in_progress' : 'upcoming'} testId="client-intervention-status-badge" />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-base text-muted-foreground">
              {!ev.fullday && <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {fmtTime(ev.start)}{ev.end ? ` – ${fmtTime(ev.end)}` : ''}{ev.duration_min ? ` (${fmtMinutes(ev.duration_min)})` : ''}</span>}
              {ev.easer_name && <span className="inline-flex items-center gap-1"><UserRound className="h-4 w-4" /> <span className="notranslate">{ev.easer_name}</span></span>}
              {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {ev.location}</span>}
            </div>
            {scope === 'past' && <InterventionPhotos ev={ev} />}
            <QualityControlBlock qc={ev.quality_control} label={ev.label} />
            {scope === 'past' && (
              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                {ev.rating ? (
                  <div className="text-base" data-testid="client-intervention-rating">
                    <Stars value={ev.rating.stars} size="h-5 w-5" />
                    {ev.rating.comment && <p className="text-muted-foreground mt-1 italic">« {ev.rating.comment} »</p>}
                  </div>
                ) : <p className="text-base text-muted-foreground">Comment s'est passée cette prestation ?</p>}
                {!ev.rating && <Button className="h-11" onClick={() => setRating(ev)} data-testid="client-intervention-rate-button"><Star className="h-4 w-4 mr-2" /> Évaluer</Button>}
              </div>
            )}
          </Card>
        );
      })}

      <RatingDialog event={rating} onClose={() => setRating(null)} onDone={() => { setRating(null); toast.success('Merci pour votre avis !'); load(); }} onUnauthorized={onUnauthorized} />
    </div>
  );
}
